import './table-pma.scss';
import React, {useEffect, useState} from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {useTranslation} from 'react-i18next';
import apiClient from '../../../../utils/apiClient';
import Grid from '@mui/material/Grid2';
import decodeToken from '../../../../utils/decode-token';
import {Alert, Snackbar} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import { MARKETS } from '../../../../constants/constants';
import SortableHeaderCell from '../../common/table/sortable-header-cell';
import RowActionsMenu from '../../common/table/row-actions-menu';
import TableResultsSummary from '../../common/table/table-results-summary';
import handleEmptyTableResponse from '../../common/table/utils/handle-empty-table-response';
import {
  compareText,
  getNextSortDirection,
  getStoredFilters,
  includesNormalized,
  saveFilters,
  sortCountryCodesByLabel,
  sortRows,
  uniqueTruthyValues,
} from '../../../../utils/tableUtils';

const FILTERS_STORAGE_KEY = 'pma-pma-filters';
const INITIAL_FILTERS = {
  name: '',
  market: '',
  country: '',
};
const SORT_FIELDS = {
  NAME: 'name',
  LAST_NAME: 'last_name',
};
const SORT_COMPARATORS = {
  [SORT_FIELDS.NAME]: (a, b) => compareText(a.name, b.name),
  [SORT_FIELDS.LAST_NAME]: (a, b) => compareText(a.last_name, b.last_name),
};

function TablePma({reload, onDataLoaded, showSummary = true, onSummaryChange = () => {}}) {
  const {t} = useTranslation();
  const token = sessionStorage.getItem('token') || null;
  const role = decodeToken(token).role;
  const [dataLoaded, setDataLoaded] = useState(false);
  const navigate = useNavigate();

  const [pmas, setPmas] = useState([]);
  const [filteredPmas, setFilteredPmas] = useState([]);
  const [countries, setCountries] = useState([]);
  const [markets] = useState(MARKETS);

  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Estado de orden
  const [order, setOrder] = useState('');
  const [orderBy, setOrderBy] = useState('');

  const getPmaCountryCodes = (pma) => {
    const managedCodes = (pma?.managed_area_codes || '')
      .split(',')
      .map((code) => code.trim())
      .filter(Boolean);

    if (managedCodes.length > 0) {
      return managedCodes;
    }

    return pma?.area_code ? [pma.area_code] : [];
  };

  const handleRequestSort = (property) => {
    setOrder(getNextSortDirection(orderBy, order, property));
    setOrderBy(property);
  };

  const sortData = (array) => sortRows(array, orderBy, order, SORT_COMPARATORS);

  const fetchData = async () => {
    const authToken = token;
    const url = `${process.env.REACT_APP_API_URL}/admin/pmas`;

    const config = {
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Cache-Control': 'no-cache',
      },
    };

    try {
      const response = await apiClient.get(url, config);
      if (response.status === 200) {
        const data = response.data;

        // 👉 Igual que en el componente pma (clave distinta en sessionStorage si quieres)
        const initialFilters = getStoredFilters(FILTERS_STORAGE_KEY, INITIAL_FILTERS);

        setFilters(initialFilters);
        setPmas(data);
        applyFilters(initialFilters, data);
        setDataLoaded(true);
        onDataLoaded();

        const market = markets.find((m) => m.name === initialFilters.market);
        const availableCountries = market
          ? market.countries
          : uniqueTruthyValues(data.flatMap((pma) => getPmaCountryCodes(pma)));

        setCountries(orderCountries(availableCountries));
      } else if (response.status === 204) {
        handleEmptyTableResponse({
          clearStateSetters: [setPmas, setFilteredPmas, setCountries],
          onDataLoaded,
          onSummaryChange,
          setDataLoaded,
        });
      }
    } catch (error) {
      if (error.response) {
        setSnackbarOpen(true);
        setSnackbarMessage(t('common-internal-server-error'));
      }
    }
  };

  useEffect(() => {
    if (reload) {
      fetchData();
    }
    // eslint-disable-next-line
  }, [reload, token, onDataLoaded, t]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    if (name === 'market') {
      const selectedMarket = markets.find((market) => market.name === value);

      const updatedCountries = selectedMarket
        ? selectedMarket.countries
        : uniqueTruthyValues(pmas.flatMap((pma) => getPmaCountryCodes(pma)));

      setCountries(orderCountries(updatedCountries));

      setFilters({
        ...filters,
        market: value,
        country: '',
      });
    } else {
      setFilters({
        ...filters,
        [name]: value,
      });
    }
  };

  const applyFilters = (appliedFilters = filters, pmasData = pmas) => {
    let filtered = [...pmasData];

    if (appliedFilters.name) {
      filtered = filtered.filter(
        (pma) =>
          includesNormalized(pma.name, appliedFilters.name) ||
          includesNormalized(pma.last_name, appliedFilters.name)
      );
    }

    if (appliedFilters.market) {
      const selectedMarket = markets.find(
        (market) => market.name === appliedFilters.market
      );
      if (selectedMarket) {
        filtered = filtered.filter((pma) => {
          const countryCodes = getPmaCountryCodes(pma);
          return countryCodes.some((code) => selectedMarket.countries.includes(code));
        });
      }
    }

    if (appliedFilters.country) {
      filtered = filtered.filter((pma) => {
        const countryCodes = getPmaCountryCodes(pma);
        return countryCodes.includes(appliedFilters.country);
      });
    }

    setFilteredPmas(filtered);
    onSummaryChange({ total: filtered.length });
    saveFilters(FILTERS_STORAGE_KEY, appliedFilters);
  };

  const handleApplyFilter = () => {
    applyFilters();
  };

  const resetFilters = () => {
    const initialFilters = INITIAL_FILTERS;

    const allCountries = uniqueTruthyValues(pmas.flatMap((pma) => getPmaCountryCodes(pma)));
    setCountries(orderCountries(allCountries));

    setFilters(initialFilters);
    saveFilters(FILTERS_STORAGE_KEY, initialFilters);

    applyFilters(initialFilters, pmas);
  };

  const orderCountries = (countries) => {
    return sortCountryCodesByLabel(countries, t);
  };

  return (
    <>
      {dataLoaded ? (
        pmas && pmas.length !== 0 ? (
          <>
            <div className="filters">
              <p>{t('common-search')}</p>
              <Grid container spacing={3}  rowSpacing={0}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <div className="form-control">
                    <label htmlFor="name">
                      {t('common-name')}/{t('common-last-name')}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={filters.name}
                      onChange={handleFilterChange}
                    />
                  </div>
                </Grid>

                {(role === 'admin') && markets.length > 0 && (
                  <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <div className="form-control">
                      <label htmlFor="market">{t('common-market')}</label>
                      <select
                        name="market"
                        id="market"
                        className="normal"
                        value={filters.market}
                        onChange={handleFilterChange}
                      >
                        <option value="">{t('common-all-market')}</option>
                        {markets.map((market) => (
                          <option key={market.name} value={market.name}>
                            {t(`market-${market.name}`)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </Grid>
                )}

                {(role === 'admin' ||
                  role === 'marketAdmin' ||
                  role === 'countryAdmin') && (markets.length > 1 || countries.length > 1) && (
                  <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <div className="form-control">
                      <label htmlFor="country">{t('common-country')}</label>
                      <select
                        name="country"
                        id="country"
                        className="normal"
                        value={filters.country}
                        onChange={handleFilterChange}
                      >
                        <option value="">{t('common-all-country')}</option>
                        {countries.map((country) => (
                          <option key={country} value={country}>
                            {t(`countries-${country}`)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </Grid>
                )}
              </Grid>

              {showSummary && (
                <TableResultsSummary count={filteredPmas.length} />
              )}

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <div className="actions">
                    <button className="btn" onClick={handleApplyFilter}>
                      {t('common-apply')}
                    </button>
                  </div>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <div className="actions">
                    <button onClick={resetFilters} className="btn text narrow">
                      {t('common-reset-filters')}
                    </button>
                  </div>
                </Grid>
              </Grid>

            </div>

            <div className="table-wrapper">
              {filteredPmas.length !== 0 ? (
                <Table
                  sx={{minWidth: 650}}
                  aria-label="simple table"
                  className="user-table admin-pma"
                >
                  <TableHead>
                    <TableRow>
                      <SortableHeaderCell
                        field={SORT_FIELDS.NAME}
                        orderBy={orderBy}
                        order={order}
                        onRequestSort={handleRequestSort}
                        label={t('common-name')}
                      />
                      <SortableHeaderCell
                        field={SORT_FIELDS.LAST_NAME}
                        orderBy={orderBy}
                        order={order}
                        onRequestSort={handleRequestSort}
                        label={t('common-last-name')}
                      />
                      <TableCell align="left">
                        <span className="text">{t('common-email')}</span>
                      </TableCell>
                      <TableCell align="left">
                        <span className="text">{t('common-country')}</span>
                      </TableCell>
                      <TableCell align="left">
                        <span className="text">{t('common-hacs-asigned')}</span>
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortData(filteredPmas).map((row) => (
                      <TableRow key={row.id}>
                        <TableCell align="left">
                          <span className="text">{row.name}</span>
                        </TableCell>
                        <TableCell align="left">
                          <span className="text">{row.last_name}</span>
                        </TableCell>
                        <TableCell align="left">
                          <span className="text">{row.email}</span>
                        </TableCell>
                        <TableCell align="left">
                          <span className="text">
                            {t(`countries-${row.area_code}`)}
                          </span>
                        </TableCell>
                        <TableCell align="left">
                          <span className="text">{row.number_hac_assigned}</span>
                        </TableCell>
                        <TableCell align="right">
                          <RowActionsMenu
                            actions={[
                              {
                                key: 'edit-pma',
                                label: t('admin-main-tab2-option1'),
                                onClick: () =>
                                  navigate(`/admin/edit-pma`, {
                                    state: {
                                      id: row.id,
                                      breadcrumbName: `${row.name} ${row.last_name}`,
                                    },
                                  }),
                              },
                              {
                                key: 'delete-user',
                                label: t('admin-main-tab2-option3'),
                                onClick: () =>
                                  navigate(`/admin/delete-user`, {
                                    state: {
                                      user: row,
                                      role: 'pma',
                                    },
                                  }),
                              },
                            ]}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="no-result">
                  <h4 className="txt-center">{t('admin-main-title-empty')}</h4>
                </div>
              )}
            </div>

            <Snackbar
              open={snackbarOpen}
              autoHideDuration={3000}
              onClose={handleSnackbarClose}
            >
              <Alert severity="error" sx={{ width: '100%' }}>
                {snackbarMessage}
              </Alert>
            </Snackbar>
          </>
        ) : (
          <div className="no-result">
            <h2 className="txt-center">
              {t('admin-main-tab2-title-empty')}
              <small>{t('admin-main-tab2-text-empty')}</small>
            </h2>
          </div>
        )
      ) : (
        <div>{t('common-loading')}</div>
      )}
    </>
  );
}

export default TablePma;
